import React from 'react';
import {Platform, useWindowDimensions, View} from 'react-native';
import {useSelector} from 'react-redux';

import Carousel from '/component/base/Carousel';
import LoadingIndicator from '/component/base/LoadingIndicator';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import BaseScreen from '/component/partial/BaseScreen';
import {PeopleToContactSection} from '/component/partial/PeopleToContactSection';
import Timestamp from '/component/partial/Timestamp';
import {MAX_CONTENT_WIDTH} from '/constant/data.constant';
import {indent} from '/constant/layout.constant';
import {ClientsID} from '/constant/TestIDs';
import {Shared} from '/constant/TestIDs';
import {CLIENTS_SCREEN} from '/enum/screen.enum';
import {SCREEN_NAMES} from '/enum/tealium.enum';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {shouldReleaseFeature} from '/util/featureRelease.util';

import SharePlanSection from './SharePlanSection/SharePlanSection';
import {useClients} from './Clients.hook';
import {styles} from './Clients.style';
import ClientsOverviewSection from './ClientsOverviewSection';
import ClientsSummaryCard from './ClientsSummaryCard';
import ExpiringSection from './ExpiringSection';
import OrdersMetricsSection from './OrdersMetricsSection';
import OrdersSummaryCard from './OrdersSummaryCard';
import PremierVolumeSection from './PremierVolumeSection';

const WEB_CARD_GAP = 5;
const WEB_CAROUSEL_HORIZONTAL_PADDING = indent + indent * 0.75;
interface ClientsProps {
  showChatFeature: boolean;
  hasClientMetricsById: boolean;
  getClientMetrics: () => void;
  onNavigationIconPress: () => void;
  onShowReport: () => void;
  onShowInfoPopover: () => void;
  onShowRefreshInfoPopover: () => void;
  onOpenModal: () => void;
  onNavigate: () => void;
  allCandidatesCount: number | null;
  lastPaidRank: string;
  highestRankDescription: string;
  coachCurrentRank: string;
  userId: string;
  popoverShow: () => void;
}

export const Clients: React.FC<ClientsProps> = ({
  showChatFeature,
  hasClientMetricsById = false,
  getClientMetrics = () => {},
  onNavigationIconPress = () => {},
  onShowReport = () => {},
  onShowInfoPopover = () => {},
  onShowRefreshInfoPopover = () => {},
  onOpenModal = () => {},
  onNavigate = () => {},
  lastPaidRank = '',
  highestRankDescription = '',
  coachCurrentRank = '',
  userId = '',
}) => {
  const {tt} = useClients({hasClientMetricsById, getClientMetrics});
  const featureFlag = useSelector(variablesFeatureFlag);
  const {width} = useWindowDimensions();

  //If showCandidateManagement flag is set to false, hide candidate section

  //If showAddCandidate flag is set to true & showCandidateManagement is true
  //display candidate section

  //If showAddCandidate flag is set to false & showCandidateManagement is true,
  // if candidate count is null or 0, hide candidate section.

  const {ff_share_bundle = ''} = featureFlag;
  const share_optavia_plan_ff = shouldReleaseFeature(ff_share_bundle);
  const IsWeb = Platform.OS === 'web';
  const isSmallScreen = width < 750;
  const availableWidth =
    IsWeb && !isSmallScreen ? Math.min(width, MAX_CONTENT_WIDTH) : width - 80;
  const webMultiCardAvailableWidth = Math.max(
    0,
    availableWidth - WEB_CAROUSEL_HORIZONTAL_PADDING,
  );
  const webSummaryCardWidth = (webMultiCardAvailableWidth - WEB_CARD_GAP) / 2;
  const ScreenOSBasedWidth =
    IsWeb && !isSmallScreen ? MAX_CONTENT_WIDTH / 2.2 : availableWidth;
  const OrdersCardHeight = 180;

  return (
    <BaseScreen
      testID={ClientsID.clientsScreen}
      showChatFeature={showChatFeature}
      onNavigationIconPress={onNavigationIconPress}
      style={styles.content}>
      <View style={styles.headerMargin} />
      <Gutter style={styles.header}>
        <Text
          accessible={true}
          accessibilityRole="header"
          accessibilityLabel="Clients"
          variant="xLarge"
          style={styles.headerText}
          testID={Shared.sharedScreenTitle}>
          {tt('header.text', 'Clients')}
        </Text>
      </Gutter>
      <View style={styles.timestampContainer}>
        <Timestamp
          onShowInfoPopover={onShowRefreshInfoPopover}
          infoButtonLabel="Show clients data refresh info"
          style={
            IsWeb
              ? {base: {marginHorizontal: 0, paddingHorizontal: indent}}
              : {}
          }
        />
      </View>
      {hasClientMetricsById ? (
        <>
          {IsWeb && !isSmallScreen ? (
            <View style={styles.webSectionSpacing}>
              <Gutter style={styles.summaryCardsContainerWeb}>
                <View style={styles.summaryCardsRow}>
                  <ClientsSummaryCard
                    style={{
                      ...styles.summaryCardWeb,
                      width: webSummaryCardWidth,
                      marginRight: WEB_CARD_GAP,
                    }}
                  />
                  <OrdersSummaryCard
                    style={{
                      ...styles.summaryCardWeb,
                      width: webSummaryCardWidth,
                    }}
                  />
                </View>
              </Gutter>
            </View>
          ) : (
            <Carousel
              cardTitles={['Clients Summary', 'Orders This Month']}
              snapToInterval={ScreenOSBasedWidth + WEB_CARD_GAP}
              indicatorDots={true}>
              <ClientsSummaryCard
                style={{
                  width: ScreenOSBasedWidth,
                }}
              />
              <OrdersSummaryCard
                style={{
                  width: ScreenOSBasedWidth,
                  height: OrdersCardHeight,
                }}
              />
            </Carousel>
          )}
          <View style={IsWeb ? styles.webSectionSpacing : undefined}>
            <ClientsOverviewSection
              style={IsWeb ? styles.clientsOverviewSectionWeb : undefined}
              onShowReport={onShowReport}
              onShowInfoPopover={onShowInfoPopover}
            />
          </View>
        </>
      ) : (
        <LoadingIndicator style={styles.loadingIndicator} />
      )}

      {share_optavia_plan_ff && (
        <View
          style={
            IsWeb ? styles.webSectionSpacing : styles.mobileSharePlanSpacing
          }>
          <SharePlanSection
            onShowReport={onShowReport}
            onShowInfoPopover={onShowInfoPopover}
            onNavigate={onNavigate}
            lastPaidRank={lastPaidRank}
            highestAchievedRank={highestRankDescription}
            coachCurrentRank={coachCurrentRank}
            coachID={userId}
          />
        </View>
      )}
      <View
        style={
          IsWeb
            ? [
                styles.peopleToContactSection,
                styles.peopleToContactSectionWeb,
                styles.webSectionSpacing,
              ]
            : styles.peopleToContactSection
        }>
        <PeopleToContactSection
          onShowReport={onShowReport}
          onShowInfoPopover={onShowInfoPopover}
          onOpenModal={onOpenModal}
          parentScreenName={SCREEN_NAMES.clients}
        />
      </View>
      {hasClientMetricsById ? (
        <>
          <View style={IsWeb ? styles.webSectionSpacingTight : undefined}>
            <OrdersMetricsSection
              onShowReport={onShowReport}
              onShowInfoPopover={onShowInfoPopover}
            />
          </View>
          <View style={IsWeb ? styles.webSectionSpacingTight : undefined}>
            <PremierVolumeSection
              onShowReport={onShowReport}
              onShowInfoPopover={onShowInfoPopover}
            />
          </View>
          <View style={IsWeb ? styles.webSectionSpacingTight : undefined}>
            <ExpiringSection
              onShowReport={onShowReport}
              onShowInfoPopover={onShowInfoPopover}
              screenName={CLIENTS_SCREEN}
            />
          </View>
        </>
      ) : (
        <LoadingIndicator style={styles.loadingIndicator} />
      )}
    </BaseScreen>
  );
};

export default Clients;
